import React, { useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useNotifications } from "../components/NotificationsContext";
import "../components/AppLayout.css";

const INITIAL_POSTS = [
  {
    id: 1,
    author: "Amicale Autoroutes",
    role: "Bureau executif",
    category: "Engagement",
    time: "Aujourd'hui 09:10",
    title: "Nouveaux engagements disponibles",
    body: "Les formulaires Ooredoo, Tunisianet, Taki Academie, Atome et les accords Gym sont disponibles pour les adherents actifs.",
    likes: 34,
    liked: false,
    comments: [
      { id: 101, author: "Omar Amin", text: "Merci pour la mise a jour." },
      { id: 102, author: "Nadia Adherent", text: "Tres clair." },
    ],
  },
  {
    id: 2,
    author: "Service Evenements",
    role: "Administration",
    category: "Evenement",
    time: "Hier 17:35",
    title: "Inscription aux prochains evenements",
    body: "Les evenements ouverts affichent maintenant la capacite, le prix net, la subvention et les cibles autorisees.",
    likes: 21,
    liked: false,
    comments: [{ id: 201, author: "Chokri Adherent", text: "Parfait pour suivre les places." }],
  },
  {
    id: 3,
    author: "Support Amicale",
    role: "Assistance",
    category: "Famille",
    time: "12 juin 2026",
    title: "Mise a jour des dossiers famille",
    body: "Les adherents peuvent demander une correction famille. Les admins valident les changements depuis l'espace demandes famille.",
    likes: 18,
    liked: false,
    comments: [],
  },
  {
    id: 4,
    author: "Fournisseurs",
    role: "Portail partenaire",
    category: "Fournisseur",
    time: "10 juin 2026",
    title: "Traitement fournisseur",
    body: "Les demandes peuvent etre transmises aux fournisseurs, puis acceptees ou refusees avec un motif visible dans l'historique.",
    likes: 27,
    liked: false,
    comments: [],
  },
  {
    id: 5,
    author: "Amicale Autoroutes",
    role: "Communication",
    category: "Annonce",
    time: "08 juin 2026",
    title: "Espace actualites",
    body: "Cette page sert de journal interne pour publier des annonces courtes, des rappels et les nouveautes de la plateforme.",
    likes: 42,
    liked: false,
    comments: [],
  },
  {
    id: 6,
    author: "Administration",
    role: "Controle",
    category: "Audit",
    time: "06 juin 2026",
    title: "Controle des workflows",
    body: "Les logs admin et notifications permettent de suivre les actions sensibles: suppression, reset fournisseur, validation ou refus.",
    likes: 15,
    liked: false,
    comments: [],
  },
];

const EMOJIS = ["\u{1F642}", "\u{1F44F}", "\u{1F49A}", "\u{1F4CC}", "\u2728", "\u{1F389}"];

function getUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function initials(name) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function displayName(user) {
  return `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.matricule || "Adherent";
}

export default function Actualites() {
  const { addNotification } = useNotifications();
  const user = useMemo(getUser, []);
  const isAdmin = Boolean(user.is_amical_admin || user.is_staff || user.is_superuser);
  const fileInputRef = useRef(null);

  const [posts, setPosts] = useState(INITIAL_POSTS);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [visible, setVisible] = useState(3);
  const [composerOpen, setComposerOpen] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [newPost, setNewPost] = useState({
    title: "",
    body: "",
    category: "Annonce",
    imageUrl: "",
    imageName: "",
  });

  const categories = useMemo(
    () => ["all", ...Array.from(new Set(posts.map((post) => post.category)))],
    [posts]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((post) => {
      const matchesCategory = category === "all" || post.category === category;
      const text = `${post.title} ${post.body} ${post.author} ${post.category}`.toLowerCase();
      return matchesCategory && (!q || text.includes(q));
    });
  }, [category, posts, query]);

  const shown = filtered.slice(0, visible);

  const toggleLike = (postId) => {
    setPosts((prev) =>
      prev.map((post) =>
        post.id === postId
          ? {
              ...post,
              liked: !post.liked,
              likes: post.liked ? Math.max(0, post.likes - 1) : post.likes + 1,
            }
          : post
      )
    );
    addNotification({
      type: "success",
      title: "Actualite",
      message: "Reaction enregistree.",
      delay: 1800,
    });
  };

  const submitComment = (postId) => {
    const text = (commentDrafts[postId] || "").trim();
    if (!text) return;

    setPosts((prev) =>
      prev.map((post) =>
        post.id === postId
          ? {
              ...post,
              comments: [
                ...post.comments,
                { id: Date.now(), author: displayName(user), text },
              ],
            }
          : post
      )
    );
    setCommentDrafts((prev) => ({ ...prev, [postId]: "" }));
    addNotification({
      type: "success",
      title: "Commentaire",
      message: "Votre commentaire a ete ajoute.",
      delay: 2200,
    });
  };

  const addPost = () => {
    if (!newPost.title.trim() || !newPost.body.trim()) {
      addNotification({
        type: "warning",
        title: "Actualite",
        message: "Titre et contenu sont obligatoires.",
      });
      return;
    }

    setPosts((prev) => [
      {
        id: Date.now(),
        author: displayName(user),
        role: "Admin amicale",
        category: newPost.category,
        time: "Maintenant",
        title: newPost.title.trim(),
        body: newPost.body.trim(),
        imageUrl: newPost.imageUrl,
        imageName: newPost.imageName,
        likes: 0,
        liked: false,
        comments: [],
      },
      ...prev,
    ]);
    setNewPost({ title: "", body: "", category: "Annonce", imageUrl: "", imageName: "" });
    setComposerOpen(false);
    setShowEmojis(false);
    setVisible((value) => Math.max(value, 3));
    addNotification({
      type: "success",
      title: "Actualite publiee",
      message: "Le post est visible dans le journal.",
    });
  };

  const addEmoji = (emoji) => {
    setNewPost((prev) => ({ ...prev, body: `${prev.body}${prev.body ? " " : ""}${emoji}` }));
    setComposerOpen(true);
  };

  const attachImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setNewPost((prev) => ({
        ...prev,
        imageUrl: typeof reader.result === "string" ? reader.result : "",
        imageName: file.name,
      }));
    };
    reader.readAsDataURL(file);
    setComposerOpen(true);
    event.target.value = "";
  };

  const removeImage = () => {
    setNewPost((prev) => ({ ...prev, imageUrl: "", imageName: "" }));
  };

  return (
    <Container fluid className="actualites-page">
      <Row className="g-4">
        <Col lg={8}>
          <div className="actualites-header">
            <div>
              <span className="profile-eyebrow">Journal interne</span>
              <h3>Actualites</h3>
              <p>Posts courts, annonces et nouveautes pour les adherents.</p>
            </div>
          </div>

          {isAdmin && (
            <Card className="actualites-composer mb-3">
              <Card.Body>
                <div className="actualites-composer__avatar">
                  {initials(displayName(user))}
                </div>
                <div className="actualites-composer__form">
                  <button
                    type="button"
                    className="actualites-composer__prompt"
                    onClick={() => setComposerOpen(true)}
                  >
                    Quoi de neuf, {displayName(user).split(" ")[0]} ?
                  </button>

                  {composerOpen && (
                    <div className="actualites-composer__editor">
                      <Form.Control
                        className="mb-2"
                        value={newPost.title}
                        onChange={(event) => setNewPost((prev) => ({ ...prev, title: event.target.value }))}
                        placeholder="Titre de l'actualite"
                      />
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={newPost.body}
                        onChange={(event) => setNewPost((prev) => ({ ...prev, body: event.target.value }))}
                        placeholder="Ecrire une actualite..."
                      />

                      {newPost.imageUrl && (
                        <div className="actualites-composer__preview">
                          <img src={newPost.imageUrl} alt={newPost.imageName || "Apercu"} />
                          <button type="button" onClick={removeImage} aria-label="Retirer l'image">
                            x
                          </button>
                        </div>
                      )}

                      {showEmojis && (
                        <div className="actualites-emoji-panel">
                          {EMOJIS.map((emoji) => (
                            <button type="button" key={emoji} onClick={() => addEmoji(emoji)}>
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="actualites-composer__toolbar">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={attachImage}
                    />
                    <button type="button" onClick={() => fileInputRef.current?.click()} title="Ajouter une image">
                      <span aria-hidden="true">+</span>
                      Image
                    </button>
                    <button type="button" onClick={() => setShowEmojis((value) => !value)} title="Ajouter un sourire">
                      <span aria-hidden="true">:)</span>
                      Smile
                    </button>
                    <Form.Select
                      value={newPost.category}
                      onChange={(event) => setNewPost((prev) => ({ ...prev, category: event.target.value }))}
                    >
                      <option>Annonce</option>
                      <option>Evenement</option>
                      <option>Engagement</option>
                      <option>Famille</option>
                      <option>Audit</option>
                    </Form.Select>
                    <Button variant="primary" onClick={addPost}>
                      Publier
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          <div className="actualites-feed">
            {shown.map((post) => (
              <article className="actualites-post" key={post.id}>
                <header>
                  <div className="actualites-avatar">{initials(post.author)}</div>
                  <div>
                    <strong>{post.author}</strong>
                    <span>{post.role} - {post.time}</span>
                  </div>
                  <Badge bg="light" text="dark">{post.category}</Badge>
                </header>

                <h4>{post.title}</h4>
                <p>{post.body}</p>

                {post.imageUrl && (
                  <div className="actualites-post__image">
                    <img src={post.imageUrl} alt={post.imageName || post.title} />
                  </div>
                )}

                <div className="actualites-post__stats">
                  <span>{post.likes} reaction{post.likes > 1 ? "s" : ""}</span>
                  <span>{post.comments.length} commentaire{post.comments.length > 1 ? "s" : ""}</span>
                </div>
                <footer>
                  <button
                    type="button"
                    className={post.liked ? "active" : ""}
                    onClick={() => toggleLike(post.id)}
                  >
                    {post.liked ? "Aime" : "J'aime"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById(`comment-${post.id}`);
                      if (input) input.focus();
                    }}
                  >
                    Commenter
                  </button>
                </footer>

                <div className="actualites-comments">
                  {post.comments.map((comment) => (
                    <div className="actualites-comment" key={comment.id}>
                      <strong>{comment.author}</strong>
                      <span>{comment.text}</span>
                    </div>
                  ))}
                  <div className="actualites-comment-form">
                    <Form.Control
                      id={`comment-${post.id}`}
                      value={commentDrafts[post.id] || ""}
                      onChange={(event) =>
                        setCommentDrafts((prev) => ({ ...prev, [post.id]: event.target.value }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          submitComment(post.id);
                        }
                      }}
                      placeholder="Ecrire un commentaire..."
                    />
                    <Button variant="outline-primary" onClick={() => submitComment(post.id)}>
                      Envoyer
                    </Button>
                  </div>
                </div>
              </article>
            ))}

            {shown.length === 0 && (
              <Card>
                <Card.Body className="text-muted">Aucune actualite trouvee.</Card.Body>
              </Card>
            )}

            {visible < filtered.length && (
              <div className="text-center mt-3">
                <Button variant="outline-primary" onClick={() => setVisible((value) => value + 3)}>
                  Charger plus
                </Button>
              </div>
            )}
          </div>
        </Col>

        <Col lg={4}>
          <Card className="actualites-side">
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Recherche</Form.Label>
                <Form.Control
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setVisible(3);
                  }}
                  placeholder="Chercher une actualite..."
                />
              </Form.Group>

              <div className="actualites-categories">
                {categories.map((item) => (
                  <button
                    type="button"
                    key={item}
                    className={item === category ? "active" : ""}
                    onClick={() => {
                      setCategory(item);
                      setVisible(3);
                    }}
                  >
                    {item === "all" ? "Tous" : item}
                  </button>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
